import React, { useMemo, useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import APIHelper from "../utils/APIHelper";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GetAppIcon from "@mui/icons-material/GetApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CarrierServiceDialog from "../components/CarrierServiceDialog";

// Shipping-related statuses (excluding fulfilled - those are not shown in Shipping Page)
const SHIPPING_STATUSES = [
  "ready_for_shipping",
  "shipping_label_generated",
  "shipped",
];

export default function ShippingPage({ token, pageId }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("shippedAt");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [generatingLabel, setGeneratingLabel] = useState(null);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [selectedOrderForLabel, setSelectedOrderForLabel] = useState(null);
  const [selectedCarrier, setSelectedCarrier] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [availableCarriers, setAvailableCarriers] = useState([]);
  const [loadingServiceability, setLoadingServiceability] = useState(false);
  const [quickQuoteRates, setQuickQuoteRates] = useState([]);
  const [loadingQuickQuote, setLoadingQuickQuote] = useState(false);
  const [packageWeight, setPackageWeight] = useState(null);
  const [packageDimensions, setPackageDimensions] = useState({
    length: null,
    width: null,
    height: null,
  });
  const [selectedRate, setSelectedRate] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedOrderForMenu, setSelectedOrderForMenu] = useState(null);
  const [cancellingShipment, setCancellingShipment] = useState(null); // Track which order is cancelling shipment

  // Custom shipment states
  const [customShipDialogOpen, setCustomShipDialogOpen] = useState(false);
  const [customCarrier, setCustomCarrier] = useState("");
  const [customTrackingNumber, setCustomTrackingNumber] = useState("");
  const [customTrackingUrl, setCustomTrackingUrl] = useState("");
  const [selectedOrderForCustomShip, setSelectedOrderForCustomShip] = useState(null);
  const [submittingCustomShip, setSubmittingCustomShip] = useState(false);

  useEffect(() => {
    if (token && pageId) {
      fetchShippingOrders();
    }
  }, [token, pageId, query, statusFilter, page, rowsPerPage]);

  const fetchShippingOrders = async () => {
    try {
      setLoading(true);
      // Fetch all orders and filter for shipping-related statuses
      const allOrders = [];

      // Fetch orders for each shipping status (only fetch what we need based on filter)
      const statusesToFetch =
        statusFilter === "All" ? SHIPPING_STATUSES : [statusFilter];

      // Fetch in parallel for better performance
      const fetchPromises = statusesToFetch.map((status) =>
        APIHelper.listOrders(token, {
          pageId,
          status: status,
          search: query || undefined,
          page: 1,
          limit: 1000, // Get all to filter client-side
        })
      );

      const responses = await Promise.all(fetchPromises);
      responses.forEach((response) => {
        if (response.orders) {
          allOrders.push(...response.orders);
        }
      });

      // Remove duplicates (in case an order appears in multiple status queries)
      let filteredOrders = Array.from(
        new Map(allOrders.map((o) => [o.orderId, o])).values()
      );

      // Apply status filter if not "All"
      if (statusFilter !== "All") {
        filteredOrders = filteredOrders.filter(
          (o) => o.status === statusFilter
        );
      }

      // Ensure we only show shipping-related statuses (exclude fulfilled)
      filteredOrders = filteredOrders.filter(
        (o) => SHIPPING_STATUSES.includes(o.status) && o.status !== "fulfilled"
      );

      // Apply search filter if query exists
      if (query) {
        const q = query.toLowerCase();
        filteredOrders = filteredOrders.filter(
          (o) =>
            o.orderId?.toLowerCase().includes(q) ||
            o.trackingNumber?.toLowerCase().includes(q) ||
            o.carrier?.toLowerCase().includes(q) ||
            o.customerName?.toLowerCase().includes(q) ||
            o.productName?.toLowerCase().includes(q) ||
            `${o.shippingAddress?.city || ""} ${o.shippingAddress?.state || ""}`
              .toLowerCase()
              .includes(q)
        );
      }

      // Sort orders
      filteredOrders.sort((a, b) => {
        const dir = sortDir === "asc" ? 1 : -1;
        let av = a[sortBy];
        let bv = b[sortBy];

        // Handle date sorting
        if (sortBy === "shippedAt" || sortBy === "createdAt") {
          av = av ? new Date(av).getTime() : 0;
          bv = bv ? new Date(bv).getTime() : 0;
        } else if (typeof av === "string") {
          av = av.toLowerCase();
        }
        if (typeof bv === "string") {
          bv = bv.toLowerCase();
        }

        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });

      // Paginate
      const start = page * rowsPerPage;
      const paged = filteredOrders.slice(start, start + rowsPerPage);

      setOrders(paged);
      setTotal(filteredOrders.length);
    } catch (error) {
      console.error("Error fetching shipping orders:", error);
      toast.error("Failed to fetch shipping orders");
    } finally {
      setLoading(false);
    }
  };

  const statusChip = (status) => {
    const statusMap = {
      ready_for_shipping: { label: "Ready for Shipping", color: "info" },
      shipping_label_generated: {
        label: "Label Generated",
        color: "info",
      },
      shipped: { label: "Shipped", color: "info" },
      fulfilled: { label: "Fulfilled", color: "success" },
    };
    const config = statusMap[status?.toLowerCase()] || {
      label: status,
      color: "default",
    };
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const formatDestination = (shippingAddress) => {
    if (!shippingAddress) return "N/A";
    const parts = [
      shippingAddress.city,
      shippingAddress.state,
      shippingAddress.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("desc");
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      setUpdatingStatus(orderId);
      await APIHelper.updateOrderStatus(token, orderId, newStatus);
      toast.success(`Order status updated to ${newStatus}`);
      fetchShippingOrders();
    } catch (error) {
      toast.error("Failed to update order status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleOpenLabelDialog = async (orderId) => {
    // Find the order to check if it already has a shipmentId
    const order = orders.find((o) => o.orderId === orderId);
    
    // If order already has a shipmentId, skip serviceability check and generate label directly
    // Note: generateLabelUrl only needs the orderId, not carrier/service
    if (order?.shipmentId) {
      try {
        setGeneratingLabel(orderId);
        // When we have a shipmentId, we don't need to pass carrier/service
        // The backend will use the saved values from the order if needed
        const response = await APIHelper.generateShippingLabel(
          token,
          orderId,
          {
            // Pass empty strings - backend will use saved values from order if shipmentId exists
            carrier: "",
            service: "",
          }
        );
        toast.success("Shipping label generated successfully!");
        fetchShippingOrders();
      } catch (error) {
        toast.error(
          error.response?.data?.details ||
            error.response?.data?.error ||
            "Failed to generate shipping label"
        );
      } finally {
        setGeneratingLabel(null);
      }
      return; // Skip opening the dialog
    }
    
    // If no shipmentId, proceed with normal flow (check serviceability, fetch quick quote and show dialog)
    setSelectedOrderForLabel(orderId);
    setSelectedCarrier("");
    setSelectedService("");
    setAvailableCarriers([]);
    setQuickQuoteRates([]);
    setPackageWeight(null);
    setPackageDimensions({ length: null, width: null, height: null });
    setLabelDialogOpen(true);
    
    // Check serviceability for this order
    try {
      setLoadingServiceability(true);
      const response = await APIHelper.checkServiceability(token, orderId);
      if (response.success && response.availableCarriers) {
        // Map carrier names to match our CARRIER_SERVICES keys
        const carrierMap = {
          bluedart: "Bluedart",
          zippyybluedart: "ZippyyBluedart",
          delhivery: "Delhivery",
          dtdc: "DTDC",
          ecom: "Ecom",
          xpressbees: "Xpressbees",
          "dtdc-b2b": "DTDC-B2B",
          ats: "ATS",
          zippyyxekart: "ZippyyxEkart",
          ekartpremium: "EkartPremium",
          shadowfax: "Shadowfax",
          blitz: "Blitz",
        };
        
        const available = response.availableCarriers
          .map((c) => {
            const normalized = c.carrier.toLowerCase();
            return carrierMap[normalized] || c.carrier;
          })
          .filter(Boolean);
        
        setAvailableCarriers(available);
      }
    } catch (error) {
      console.error("Error checking serviceability:", error);
      // If serviceability check fails, show all carriers (fallback)
      setAvailableCarriers([]);
    } finally {
      setLoadingServiceability(false);
    }

    // Fetch quick quote rates for this order
    try {
      setLoadingQuickQuote(true);
      const quickQuoteResponse = await APIHelper.getQuickQuote(token, orderId);
      if (
        quickQuoteResponse?.success &&
        Array.isArray(quickQuoteResponse.rates)
      ) {
        setQuickQuoteRates(quickQuoteResponse.rates);
      } else {
        setQuickQuoteRates([]);
      }

      if (quickQuoteResponse?.params) {
        const p = quickQuoteResponse.params;
        setPackageWeight(p.weightKg ?? 1);
        setPackageDimensions({
          length: p.dimensionsCm?.length ?? 12,
          width: p.dimensionsCm?.width ?? 12,
          height: p.dimensionsCm?.height ?? 12,
        });
      }
    } catch (error) {
      console.error("Error fetching quick quote rates:", error);
      setQuickQuoteRates([]);
    } finally {
      setLoadingQuickQuote(false);
    }
  };

  const handleCloseLabelDialog = () => {
    setLabelDialogOpen(false);
    setSelectedOrderForLabel(null);
    setSelectedCarrier("");
    setSelectedService("");
    setAvailableCarriers([]);
    setLoadingServiceability(false);
    setQuickQuoteRates([]);
    setLoadingQuickQuote(false);
    setPackageWeight(null);
    setPackageDimensions({ length: null, width: null, height: null });
    setSelectedRate(null);
  };

  const handlePackageChange = async (pkg) => {
    setPackageWeight(pkg.weightKg);
    setPackageDimensions({
      length: pkg.length,
      width: pkg.width,
      height: pkg.height,
    });

    if (!selectedOrderForLabel) return;

    try {
      setLoadingQuickQuote(true);
      const quickQuoteResponse = await APIHelper.getQuickQuote(
        token,
        selectedOrderForLabel,
        {
          packageWeight: pkg.weightKg,
          packageDimensions: {
            length: pkg.length,
            width: pkg.width,
            height: pkg.height,
          },
        }
      );
      if (
        quickQuoteResponse?.success &&
        Array.isArray(quickQuoteResponse.rates)
      ) {
        setQuickQuoteRates(quickQuoteResponse.rates);
      } else {
        setQuickQuoteRates([]);
      }
    } catch (error) {
      console.error("Error refreshing quick quote rates:", error);
    } finally {
      setLoadingQuickQuote(false);
    }
  };

  const handleGenerateShippingLabel = async () => {
    if (!selectedCarrier || !selectedService) {
      toast.error("Please select both carrier and service");
      return;
    }

    try {
      setGeneratingLabel(selectedOrderForLabel);
      const response = await APIHelper.generateShippingLabel(
        token,
        selectedOrderForLabel,
        {
          carrier: selectedCarrier,
          service: selectedService,
          shippingRate: selectedRate || undefined,
          packageWeight: packageWeight,
          packageDimensions: packageDimensions,
        }
      );
      toast.success("Shipping label generated successfully!");
      fetchShippingOrders();
      handleCloseLabelDialog();
    } catch (error) {
      toast.error(
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Failed to generate shipping label"
      );
    } finally {
      setGeneratingLabel(null);
    }
  };

  const handleTrackShipment = async (orderId) => {
    try {
      setTrackingOrder(orderId);
      const response = await APIHelper.trackShipment(token, orderId);
      toast.success("Tracking information retrieved");
      fetchShippingOrders(); // Refresh to update status if needed
    } catch (error) {
      toast.error(
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Failed to track shipment"
      );
    } finally {
      setTrackingOrder(null);
    }
  };

  const handleCancelShipment = async (orderId) => {
    try {
      setCancellingShipment(orderId);
      await APIHelper.cancelShipment(token, orderId);
      toast.success("Shipment cancelled successfully");
      fetchShippingOrders(); // Refresh to update status
    } catch (error) {
      toast.error(
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Failed to cancel shipment"
      );
    } finally {
      setCancellingShipment(null);
      handleMenuClose();
    }
  };

  const handleOpenCustomShipDialog = (orderId) => {
    const order = orders.find((o) => o.orderId === orderId);
    setSelectedOrderForCustomShip(orderId);
    setCustomCarrier(order?.carrier || "");
    setCustomTrackingNumber(order?.trackingNumber || "");
    setCustomTrackingUrl(order?.trackingUrl || "");
    setCustomShipDialogOpen(true);
  };

  const handleCustomShipSubmit = async () => {
    if (!customCarrier.trim()) {
      toast.error("Carrier is required");
      return;
    }

    try {
      setSubmittingCustomShip(true);
      await APIHelper.customShipment(token, selectedOrderForCustomShip, {
        carrier: customCarrier.trim(),
        trackingNumber: customTrackingNumber.trim() || undefined,
        trackingUrl: customTrackingUrl.trim() || undefined,
      });
      toast.success("Custom shipping details updated successfully");
      setCustomShipDialogOpen(false);
      fetchShippingOrders();
    } catch (error) {
      console.error("Error updating custom shipment:", error);
      toast.error(error.response?.data?.error || "Failed to update custom shipment");
    } finally {
      setSubmittingCustomShip(false);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedOrderForMenu(null);
  };

  return (
    <Box>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <LocalShippingIcon className="text-primary-600 dark:text-primary-400" />
        <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
          Shipping
        </Typography>
      </div>

      {/* Search and Filter */}
      <div className="card-modern p-4 mt-6">
        <Typography variant="body2" className="text-gray-600 dark:text-gray-400 mb-4">
          Track and manage outgoing shipments
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
          <div className="md:col-span-4">
            <TextField
              fullWidth
              size="small"
              label="Search Order ID / Tracking / Carrier / Destination"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              className="bg-white dark:bg-gray-800"
            />
          </div>
          <div className="md:col-span-3">
            <FormControl fullWidth size="small">
              <InputLabel id="ship-status-label" className="text-gray-700 dark:text-gray-300">Status</InputLabel>
              <Select
                labelId="ship-status-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                className="bg-white dark:bg-gray-800"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="ready_for_shipping">Ready for Shipping</MenuItem>
                <MenuItem value="shipping_label_generated">
                  Label Generated
                </MenuItem>
                <MenuItem value="shipped">Shipped</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      {/* Shipments Table */}
      <div className="card-modern p-4 mt-6">
        <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          Shipments
        </Typography>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {orders.length === 0 && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl mb-4">
                <div className="flex items-center gap-3">
                  <LocalShippingIcon className="text-blue-600 dark:text-blue-400" />
                  <div>
                    <Typography variant="body2" className="font-semibold text-blue-900 dark:text-blue-200 mb-1">
                      No shipping orders found
                    </Typography>
                    <Typography variant="body2" className="text-blue-800 dark:text-blue-200">
                      {query || statusFilter !== "All"
                        ? "Try adjusting your search or filter criteria."
                        : "Orders with shipping-related statuses (Ready for Shipping, Label Generated, Shipped, Tracking, Fulfilled) will appear here."}
                    </Typography>
                  </div>
                </div>
              </div>
            )}
            {(orders.length === 0 && !query && statusFilter === "All") ? (
              // Show placeholder orders when no real orders exist
              <>
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Order ID</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Tracking #</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Carrier</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Status</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Destination</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Shipped</TableCell>
                    <TableCell align="right" className="text-gray-700 dark:text-gray-300 font-semibold">Actions</TableCell>
                  </TableRow>
                </TableHead>
                    <TableBody>
                      {[
                        {
                          orderId: "ORD-PLACEHOLDER-001",
                          trackingNumber: "TRK-1001",
                          carrier: "Delhivery",
                          status: "shipped",
                          destination: "Mumbai, Maharashtra",
                          shippedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
                        },
                        {
                          orderId: "ORD-PLACEHOLDER-002",
                          trackingNumber: "TRK-1002",
                          carrier: "BlueDart",
                          status: "tracking",
                          destination: "Delhi, Delhi",
                          shippedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
                        },
                        {
                          orderId: "ORD-PLACEHOLDER-003",
                          trackingNumber: null,
                          carrier: null,
                          status: "ready_for_shipping",
                          destination: "Bangalore, Karnataka",
                          shippedAt: null,
                        },
                      ].map((order) => (
                        <TableRow key={order.orderId} hover sx={{ opacity: 0.6 }}>
                          <TableCell>{order.orderId}</TableCell>
                          <TableCell>
                            {order.trackingNumber || (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {order.carrier || (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{statusChip(order.status)}</TableCell>
                          <TableCell>{order.destination}</TableCell>
                          <TableCell>
                            {order.shippedAt
                              ? formatDate(order.shippedAt)
                              : "N/A"}
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                justifyContent: "flex-end",
                                flexWrap: "nowrap",
                              }}
                            >
                              {order.status === "ready_for_shipping" && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disabled
                                  startIcon={<CheckCircleIcon fontSize="small" />}
                                  sx={{
                                    minWidth: "auto",
                                    fontSize: "0.75rem",
                                    px: 1,
                                  }}
                                >
                                  Generate Label
                                </Button>
                              )}
                              {order.status === "shipped" && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disabled
                                  startIcon={<VisibilityIcon fontSize="small" />}
                                  sx={{
                                    minWidth: "auto",
                                    fontSize: "0.75rem",
                                    px: 1,
                                  }}
                                >
                                  Track
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={3}
                  page={0}
                  onPageChange={() => {}}
                  rowsPerPage={10}
                  onRowsPerPageChange={() => {}}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            ) : orders.length > 0 ? (
          <>
            <TableContainer>
              <Table
                size="small"
                sx={{
                  tableLayout: "fixed", // Prevent layout shift
                  width: "100%",
                }}
              >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 160 }} className="text-gray-700 dark:text-gray-300 font-semibold">
                          <TableSortLabel
                            active={sortBy === "orderId"}
                            direction={sortBy === "orderId" ? sortDir : "asc"}
                            onClick={() => handleSort("orderId")}
                          >
                            Order ID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ width: 100 }} className="text-gray-700 dark:text-gray-300 font-semibold">
                          <TableSortLabel
                            active={sortBy === "trackingNumber"}
                            direction={
                              sortBy === "trackingNumber" ? sortDir : "asc"
                            }
                            onClick={() => handleSort("trackingNumber")}
                          >
                            Tracking #
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ width: 120 }} className="text-gray-700 dark:text-gray-300 font-semibold">
                          <TableSortLabel
                            active={sortBy === "carrier"}
                            direction={sortBy === "carrier" ? sortDir : "asc"}
                            onClick={() => handleSort("carrier")}
                          >
                            Carrier
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ width: 120 }} className="text-gray-700 dark:text-gray-300 font-semibold">
                          <TableSortLabel
                            active={sortBy === "status"}
                            direction={sortBy === "status" ? sortDir : "asc"}
                            onClick={() => handleSort("status")}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ width: 180 }} className="text-gray-700 dark:text-gray-300 font-semibold">Destination</TableCell>
                        <TableCell sx={{ width: 100 }} className="text-gray-700 dark:text-gray-300 font-semibold">
                          <TableSortLabel
                            active={sortBy === "shippedAt"}
                            direction={sortBy === "shippedAt" ? sortDir : "asc"}
                            onClick={() => handleSort("shippedAt")}
                          >
                            Shipped
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ width: 200 }} className="text-gray-700 dark:text-gray-300 font-semibold">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.orderId} hover>
                      <TableCell>{order.orderId}</TableCell>
                      <TableCell>
                        {order.trackingNumber ? (
                          order.trackingUrl ? (
                            <Link
                              href={order.trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.875rem",
                                color: "primary.main",
                                textDecoration: "underline",
                                cursor: "pointer",
                                "&:hover": {
                                  color: "primary.dark",
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 90 }}
                                title={order.trackingNumber}
                              >
                                {order.trackingNumber}
                              </Typography>
                            </Link>
                          ) : (
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.875rem",
                                maxWidth: 90,
                              }}
                              title={order.trackingNumber}
                            >
                              {order.trackingNumber}
                            </Typography>
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 110 }}
                          title={order.carrier || "N/A"}
                        >
                          {order.carrier || (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>{statusChip(order.status)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 170 }}
                          title={formatDestination(order.shippingAddress)}
                        >
                          {formatDestination(order.shippingAddress)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 90 }}
                          title={formatDate(order.shippedAt || order.createdAt)}
                        >
                          {formatDate(order.shippedAt || order.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ width: 200, paddingRight: 1 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                            flexWrap: "nowrap",
                          }}
                        >
                          {order.status === "ready_for_shipping" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenLabelDialog(order.orderId);
                              }}
                              disabled={generatingLabel === order.orderId}
                              startIcon={
                                generatingLabel === order.orderId ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <CheckCircleIcon fontSize="small" />
                                )
                              }
                              sx={{
                                minWidth: "auto",
                                fontSize: "0.75rem",
                                px: 1,
                              }}
                            >
                              Generate Label
                            </Button>
                          )}
                          {order.status === "shipping_label_generated" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateStatus(order.orderId, "shipped");
                              }}
                              disabled={updatingStatus === order.orderId}
                              startIcon={
                                updatingStatus === order.orderId ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <LocalShippingOutlinedIcon fontSize="small" />
                                )
                              }
                              sx={{
                                minWidth: "auto",
                                fontSize: "0.75rem",
                                px: 1,
                              }}
                            >
                              Ship
                            </Button>
                          )}
                          {order.status === "shipped" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateStatus(order.orderId, "fulfilled");
                              }}
                              disabled={updatingStatus === order.orderId}
                              startIcon={
                                updatingStatus === order.orderId ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <CheckCircleIcon fontSize="small" />
                                )
                              }
                              sx={{
                                minWidth: "auto",
                                fontSize: "0.75rem",
                                px: 1,
                              }}
                            >
                              Mark Fulfilled
                            </Button>
                          )}
                          {/* Overflow menu for additional actions */}
                          {(order.shippingLabelUrl || order.status === "shipped" || order.status === "shipping_label_generated" || order.status === "ready_for_shipping") && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuAnchor(e.currentTarget);
                                setSelectedOrderForMenu(order.orderId);
                              }}
                              sx={{ ml: 0.5 }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <div className="mt-4">
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                className="text-gray-700 dark:text-gray-300"
              />
            </div>
            </>
          ) : null}
          </>
        )}
      </div>

      {/* Carrier/Service Selection Dialog */}
      <CarrierServiceDialog
        open={labelDialogOpen}
        onClose={handleCloseLabelDialog}
        selectedCarrier={selectedCarrier}
        selectedService={selectedService}
        onCarrierChange={(value) => {
          setSelectedCarrier(value);
          setSelectedService(""); // Reset service when carrier changes
        }}
        onServiceChange={setSelectedService}
        availableCarriers={availableCarriers}
        loadingServiceability={loadingServiceability}
        quickQuoteRates={quickQuoteRates}
        loadingQuickQuote={loadingQuickQuote}
        packageWeight={packageWeight}
        packageDimensions={packageDimensions}
        onPackageChange={handlePackageChange}
        onRateSelect={setSelectedRate}
        generatingLabel={!!generatingLabel}
        onGenerate={handleGenerateShippingLabel}
        componentName="ShippingPage"
        productName={
          selectedOrderForLabel
            ? orders.find((o) => o.orderId === selectedOrderForLabel)
                ?.productName || null
            : null
        }
      />

      {/* Actions Overflow Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        {selectedOrderForMenu && (
          <>
            {(() => {
              const order = orders.find(
                (o) => o.orderId === selectedOrderForMenu
              );
              if (!order) return null;

              const menuItems = [];

              // Custom Shipment option
              if (["ready_for_shipping", "shipping_label_generated"].includes(order.status)) {
                menuItems.push(
                  <MenuItem
                    key="custom-shipment"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenCustomShipDialog(order.orderId);
                      handleMenuClose();
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocalShippingIcon fontSize="small" sx={{ mr: 1 }} />
                      Custom Shipment
                    </Box>
                  </MenuItem>
                );
              }

              // Download Label option
              if (order.shippingLabelUrl) {
                menuItems.push(
                  <MenuItem
                    key="download-label"
                    component={Link}
                    href={(() => {
                      if (!order.shippingLabelUrl) return null;

                      let labelUrl = order.shippingLabelUrl;

                      // Legacy fix: If URL contains Zippy shipment ID, regenerate with our orderId
                      if (labelUrl.includes("/ord_")) {
                        const backendUrl =
                          process.env.REACT_APP_BACKEND_URL ||
                          "https://instashop-6a53aa420501.herokuapp.com";
                        labelUrl = `${backendUrl}/shipping/label/${order.orderId}`;
                      }

                      // Legacy fix: Remove double /api/api/ from old URLs
                      if (labelUrl.includes("/api/api/")) {
                        labelUrl = labelUrl.replace("/api/api/", "/api/");
                      }

                      // Legacy fix: Replace www.reeltale.shop with backend URL
                      if (labelUrl.includes("reeltale.shop")) {
                        const backendUrl =
                          process.env.REACT_APP_BACKEND_URL ||
                          "https://instashop-6a53aa420501.herokuapp.com";
                        labelUrl = labelUrl.replace(/https?:\/\/[^/]+/, backendUrl);
                        labelUrl = labelUrl.replace("/api/shipping", "/shipping");
                      }

                      // Add token as query parameter for authentication
                      const token = localStorage.getItem("token");
                      if (token) {
                        const separator = labelUrl.includes("?") ? "&" : "?";
                        return `${labelUrl}${separator}token=${encodeURIComponent(token)}`;
                      }
                      return labelUrl;
                    })()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleMenuClose}
                  >
                    <GetAppIcon fontSize="small" sx={{ mr: 1 }} />
                    Download Label
                  </MenuItem>
                );
              }

              // Track option (only if tracking URL is not available)
              if (
                order.status === "shipped" &&
                order.trackingNumber &&
                !order.trackingUrl
              ) {
                menuItems.push(
                  <MenuItem
                    key="track"
                    onClick={() => {
                      handleTrackShipment(order.orderId);
                      handleMenuClose();
                    }}
                    disabled={trackingOrder === order.orderId}
                  >
                    <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
                    Track Shipment
                  </MenuItem>
                );
              }

              // Cancel Shipment option (only if shipping label is generated)
              if (order.shippingLabelUrl || order.status === "shipping_label_generated") {
                menuItems.push(
                  <MenuItem
                    key="cancel-shipment"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelShipment(order.orderId);
                    }}
                    disabled={cancellingShipment === order.orderId}
                  >
                    {cancellingShipment === order.orderId ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={14} />
                        Cancelling Shipment...
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CancelIcon fontSize="small" sx={{ mr: 1 }} />
                        Cancel Shipment
                      </Box>
                    )}
                  </MenuItem>
                );
              }

              return menuItems.length > 0 ? menuItems : null;
            })()}
          </>
        )}
      </Menu>

      {/* Custom Shipment Dialog */}
      <Dialog
        open={customShipDialogOpen}
        onClose={() => setCustomShipDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enter Custom Shipment Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Courier / Carrier"
              variant="outlined"
              fullWidth
              value={customCarrier}
              onChange={(e) => setCustomCarrier(e.target.value)}
              placeholder="e.g. FedEx, DTDC, DHL, Self"
              required
              error={!customCarrier.trim()}
              helperText={!customCarrier.trim() ? "Carrier name is required" : ""}
            />
            <TextField
              label="Tracking Number (AWB)"
              variant="outlined"
              fullWidth
              value={customTrackingNumber}
              onChange={(e) => setCustomTrackingNumber(e.target.value)}
              placeholder="e.g. 1234567890"
            />
            <TextField
              label="Tracking URL"
              variant="outlined"
              fullWidth
              value={customTrackingUrl}
              onChange={(e) => setCustomTrackingUrl(e.target.value)}
              placeholder="e.g. https://carrier.com/track/..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomShipDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleCustomShipSubmit}
            variant="contained"
            color="primary"
            disabled={submittingCustomShip || !customCarrier.trim()}
            startIcon={submittingCustomShip ? <CircularProgress size={16} /> : null}
          >
            Mark Shipped
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
